import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import {
  ArrowRight, BarChart3, Calendar, ClipboardCheck, ClipboardList, Contact, FileSignature, FolderLock,
  GraduationCap, HeartPulse, Inbox, LayoutDashboard, ListChecks, Mail, MapPin, MessageSquare, Pause, Play,
  Printer, ShieldCheck, SkipBack, SkipForward, Smartphone, Stethoscope, UserPlus, Users, Vault, Volume2, VolumeX, Wallet,
} from "lucide-react";
import chapters from "./demoChapters.json";

const ICONS = {
  LayoutDashboard, UserPlus, Users, Stethoscope, HeartPulse, FileSignature, Calendar, MapPin, Smartphone,
  ClipboardList, FolderLock, Printer, Inbox, Mail, ListChecks, Contact, Vault, ClipboardCheck, GraduationCap,
  Wallet, BarChart3, MessageSquare, ShieldCheck,
};
const FALLBACK_SECONDS = 12;

export const DEMO_CHAPTERS = chapters.map((c) => ({ ...c, icon: ICONS[c.icon] || LayoutDashboard }));

export const DEMO_TOURS = [
  { id: "full", label: "Full tour", ids: null },
  { id: "fax", label: "Fax suite", ids: ["fax-outbox", "fax-inbox", "email-to-fax", "order-tracking", "physicians"] },
  { id: "clinical", label: "Clinical", ids: ["assessments", "care-plans", "cms485", "notes", "documents"] },
];
const tourChapters = (tourId) => {
  const t = DEMO_TOURS.find((x) => x.id === tourId) || DEMO_TOURS[0];
  return t.ids ? t.ids.map((id) => DEMO_CHAPTERS.find((c) => c.id === id)).filter(Boolean) : DEMO_CHAPTERS;
};

function ChapterVisual({ chapter }) {
  const Icon = chapter.icon;
  if (chapter.image) {
    return <img key={chapter.id} src={chapter.image} alt={chapter.title} className="w-full h-full object-cover object-top" data-testid="demo-chapter-image" />;
  }
  return (
    <div className="w-full h-full bg-[radial-gradient(ellipse_at_top_left,_#0f766e_0%,_#0f172a_60%)] flex items-center justify-center p-8">
      <div className="rounded-xl bg-white/10 backdrop-blur-md border border-white/15 p-6 text-white max-w-md w-full">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-lg bg-white/15 flex items-center justify-center"><Icon className="w-6 h-6" /></div>
          <div>
            <p className="text-xs uppercase tracking-wider text-white/60">CarePilot</p>
            <p className="font-semibold">{chapter.title}</p>
          </div>
        </div>
        <div className="mt-4 space-y-2">
          {chapter.points.map((p) => (
            <div key={p} className="flex items-center gap-2 text-sm text-white/85">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-300" /> {p}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function useDemoPlayer(open, list) {
  const [audioEl, setAudioEl] = useState(null);
  const audioRef = useRef(null);
  audioRef.current = audioEl;
  const [index, setIndex] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [muted, setMuted] = useState(false);
  const [progress, setProgress] = useState(0);
  const [needsTap, setNeedsTap] = useState(false);
  const fallbackRef = useRef(null);

  const clearFallback = () => { if (fallbackRef.current) { clearInterval(fallbackRef.current); fallbackRef.current = null; } };
  const next = () => setIndex((i) => { if (i + 1 >= list.length) { setPlaying(false); return i; } return i + 1; });

  useEffect(() => { setIndex(0); setProgress(0); if (open) setPlaying(true); }, [list]);

  useEffect(() => {
    if (open) DEMO_CHAPTERS.forEach((c) => { if (c.image) { const im = new Image(); im.src = c.image; } });
  }, [open]);

  useEffect(() => {
    if (!open) { setIndex(0); setProgress(0); setPlaying(true); setNeedsTap(false); clearFallback(); return; }
    const a = audioRef.current;
    if (!a || !list[index]) return;
    clearFallback();
    setProgress(0);
    a.src = `/demo/audio/${list[index].id}.mp3`;
    a.load();
    if (playing) {
      a.play().then(() => setNeedsTap(false)).catch(() => { setNeedsTap(true); setPlaying(false); });
    }
    return clearFallback;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, index, audioEl, list]);

  useEffect(() => {
    const a = audioRef.current;
    if (!a || !open) return;
    if (playing) a.play().then(() => setNeedsTap(false)).catch(() => { setNeedsTap(true); setPlaying(false); });
    else a.pause();
  }, [playing, open, audioEl]);

  useEffect(() => { if (audioRef.current) audioRef.current.muted = muted; }, [muted, audioEl]);

  const onTime = () => { const a = audioRef.current; if (a && a.duration) setProgress(a.currentTime / a.duration); };
  const onError = () => {
    clearFallback();
    let t = 0;
    fallbackRef.current = setInterval(() => {
      if (!playing) return;
      t += 0.1; setProgress(t / FALLBACK_SECONDS);
      if (t >= FALLBACK_SECONDS) { clearFallback(); next(); }
    }, 100);
  };

  const go = (i) => { setIndex(Math.max(0, Math.min(list.length - 1, i))); setPlaying(true); };
  const toggle = () => setPlaying((p) => !p);

  return { audioRef: setAudioEl, index, playing, muted, progress, needsTap, go, toggle, next, setMuted, onTime, onError };
}

export default function WatchDemoModal({ open, onOpenChange, tour = "full", onTourChange }) {
  const [localTour, setLocalTour] = useState(tour);
  useEffect(() => { setLocalTour(tour); }, [tour, open]);
  const list = useMemo(() => tourChapters(localTour), [localTour]);
  const p = useDemoPlayer(open, list);
  const chapter = list[p.index] || list[0];
  const pickTour = (id) => { setLocalTour(id); onTourChange && onTourChange(id); };
  const minutes = Math.max(1, Math.round(list.length * 13 / 60));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl p-0 overflow-hidden" data-testid="watch-demo-modal">
        <DialogTitle className="sr-only">CarePilot platform demo</DialogTitle>
        <audio ref={p.audioRef} preload="auto" onTimeUpdate={p.onTime} onEnded={p.next} onError={p.onError} data-testid="demo-audio" />
        <div className="grid md:grid-cols-[1fr_280px] md:items-stretch">
          <div className="relative bg-slate-900">
            <div className="aspect-video relative overflow-hidden">
              <ChapterVisual chapter={chapter} />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/35 to-transparent pointer-events-none" />
              <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                <p className="text-xs uppercase tracking-wider text-emerald-300">Chapter {p.index + 1} of {list.length}</p>
                <h3 className="text-2xl font-bold mt-1" data-testid="demo-chapter-title">{chapter.title}</h3>
                <p className="mt-2 text-sm text-white/85 max-w-2xl" data-testid="demo-chapter-caption">{chapter.narration}</p>
              </div>
              {p.needsTap && (
                <button type="button" onClick={p.toggle} data-testid="demo-tap-to-play"
                  className="absolute inset-0 flex items-center justify-center bg-black/50 text-white">
                  <span className="flex items-center gap-3 rounded-full bg-white text-slate-900 px-6 py-3 font-semibold shadow-xl">
                    <Play className="w-5 h-5" /> Play with sound
                  </span>
                </button>
              )}
            </div>
            <div className="h-1 bg-white/10">
              <div className="h-full bg-emerald-400 transition-[width] duration-100" style={{ width: `${Math.min(100, p.progress * 100)}%` }} />
            </div>
            <div className="flex items-center gap-2 p-3 bg-slate-950 text-white">
              <button type="button" onClick={() => p.go(p.index - 1)} className="w-8 h-8 rounded-full hover:bg-white/10 flex items-center justify-center" data-testid="demo-prev" aria-label="Previous chapter">
                <SkipBack className="w-4 h-4" />
              </button>
              <button type="button" onClick={p.toggle} className="w-9 h-9 rounded-full bg-white text-slate-900 flex items-center justify-center" data-testid="demo-play-pause" aria-label={p.playing ? "Pause" : "Play"}>
                {p.playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 ml-0.5" />}
              </button>
              <button type="button" onClick={() => p.go(p.index + 1)} className="w-8 h-8 rounded-full hover:bg-white/10 flex items-center justify-center" data-testid="demo-next" aria-label="Next chapter">
                <SkipForward className="w-4 h-4" />
              </button>
              <div className="flex-1 flex gap-1 mx-2">
                {list.map((c, i) => (
                  <button key={c.id} type="button" onClick={() => p.go(i)} title={c.title} aria-label={c.title}
                    className={`h-1.5 flex-1 rounded-full ${i < p.index ? "bg-emerald-400" : i === p.index ? "bg-emerald-400/60" : "bg-white/20"}`} />
                ))}
              </div>
              <button type="button" onClick={() => p.setMuted(!p.muted)} className="w-8 h-8 rounded-full hover:bg-white/10 flex items-center justify-center" data-testid="demo-mute" aria-label={p.muted ? "Unmute" : "Mute"}>
                {p.muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
              </button>
              <span className="text-xs text-white/60 whitespace-nowrap">Voice-over · ~{minutes} min</span>
            </div>
          </div>
          <aside className="hidden md:flex flex-col bg-white border-l md:max-h-full">
            <div className="px-4 py-3 border-b">
              <p className="font-semibold text-slate-900">Everything in CarePilot</p>
              <div className="mt-2 flex gap-1" data-testid="demo-tour-switcher">
                {DEMO_TOURS.map((t) => (
                  <button key={t.id} type="button" onClick={() => pickTour(t.id)} data-testid={`demo-tour-${t.id}`}
                    className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${localTour === t.id ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
                    {t.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex-1 overflow-y-auto max-h-[300px] lg:max-h-[380px]">
              {list.map((c, i) => {
                const I = c.icon;
                return (
                  <button key={c.id} type="button" onClick={() => p.go(i)} data-testid={`demo-chapter-${c.id}`}
                    className={`w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm border-b border-slate-100 hover:bg-slate-50 ${i === p.index ? "bg-emerald-50 text-emerald-900 font-medium" : "text-slate-700"}`}>
                    <I className="w-4 h-4 shrink-0" /> <span className="truncate">{c.title}</span>
                  </button>
                );
              })}
            </div>
            <div className="p-4 border-t space-y-2">
              <Link to="/register" className="block">
                <Button className="w-full gap-2" data-testid="demo-start-trial-btn">Start 7-day free trial <ArrowRight className="w-4 h-4" /></Button>
              </Link>
              <p className="text-[11px] text-slate-500 text-center">Card required · cancel anytime during the trial</p>
            </div>
          </aside>
        </div>
      </DialogContent>
    </Dialog>
  );
}
